/**
 * Deposit Receipt View Modal - SOP #3
 * Displays receipt and templates for already verified deposits
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Button,
  Space,
  message,
  Card,
  Typography,
  Tabs,
  Spin,
  Input,
} from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  MessageOutlined,
  MailOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import {
  getReceiptAndTemplates,
  getDepositReceipt,
} from "../services/depositService";
import { copyToClipboard } from "../../leads/services/communicationTemplateService";

const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

const DepositReceiptViewModal = ({ visible, quote, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [whatsappCopied, setWhatsappCopied] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState("");
  const [emailTemplate, setEmailTemplate] = useState({ subject: "", body: "" });
  const [receiptPdfUrl, setReceiptPdfUrl] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const receiptPdfUrlRef = useRef(null);

  // Load receipt and templates when modal opens
  const loadReceiptAndTemplates = useCallback(async () => {
    if (!quote?.id) return;

    setLoading(true);
    try {
      const result = await getReceiptAndTemplates(quote.id);

      if (result.success && result.data) {
        setReceiptData(result.data);

        // Set templates
        if (result.data.templates) {
          setWhatsappTemplate(result.data.templates.whatsapp || "");
          setEmailTemplate(
            result.data.templates.email || { subject: "", body: "" }
          );
        }

        // Create PDF blob URL from base64
        if (result.data.receipt?.pdfBase64) {
          const pdfBlob = new Blob(
            [
              Uint8Array.from(atob(result.data.receipt.pdfBase64), (c) =>
                c.charCodeAt(0)
              ),
            ],
            { type: "application/pdf" }
          );
          const url = URL.createObjectURL(pdfBlob);
          setReceiptPdfUrl(url);
          receiptPdfUrlRef.current = url;
        }
      } else {
        message.error(result.error || "Failed to load receipt and templates");
      }
    } catch (error) {
      console.error("Error loading receipt and templates:", error);
      message.error("Failed to load receipt and templates");
    } finally {
      setLoading(false);
    }
  }, [quote?.id]);

  // Load receipt and templates when modal opens
  useEffect(() => {
    if (
      visible &&
      quote?.id &&
      (quote?.payment?.depositVerified || quote?.status === "deposit_verified")
    ) {
      loadReceiptAndTemplates();
    }

    // Cleanup when modal closes
    return () => {
      if (!visible && receiptPdfUrlRef.current) {
        URL.revokeObjectURL(receiptPdfUrlRef.current);
        receiptPdfUrlRef.current = null;
        setReceiptPdfUrl(null);
      }
    };
  }, [
    visible,
    quote?.id,
    quote?.payment?.depositVerified,
    quote?.status,
    loadReceiptAndTemplates,
  ]);

  const handleDownloadReceipt = async () => {
    if (!quote?.id) return;

    try {
      const blob = await getDepositReceipt(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename =
        receiptData?.receipt?.filename ||
        `Receipt-${receiptData?.order?.id || quote.id}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("Receipt downloaded successfully!");
    } catch (error) {
      console.error("Error downloading receipt:", error);
      message.error(error.message || "Failed to download receipt");
    }
  };

  const handleCopyWhatsApp = async () => {
    if (!whatsappTemplate) return;

    const success = await copyToClipboard(whatsappTemplate);
    if (success) {
      setWhatsappCopied(true);
      message.success("WhatsApp template copied to clipboard!");
      setTimeout(() => setWhatsappCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleCopyEmail = async () => {
    if (!emailTemplate.body) return;

    const emailText = `Subject: ${emailTemplate.subject}\n\n${emailTemplate.body}`;
    const success = await copyToClipboard(emailText);
    if (success) {
      setEmailCopied(true);
      message.success("Email template copied to clipboard!");
      setTimeout(() => setEmailCopied(false), 2000);
    } else {
      message.error("Failed to copy to clipboard");
    }
  };

  const handleClose = () => {
    if (receiptPdfUrlRef.current) {
      URL.revokeObjectURL(receiptPdfUrlRef.current);
      receiptPdfUrlRef.current = null;
      setReceiptPdfUrl(null);
    }
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <FileProtectOutlined />
          <span>Deposit Receipt & Templates</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>
            <Text>Loading receipt and templates...</Text>
          </div>
        </div>
      ) : (
        <div>
          {/* Receipt Preview */}
          {receiptPdfUrl ? (
            <Card
              title={
                <Space>
                  <FilePdfOutlined />
                  <span>Deposit Receipt</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadReceipt}
                >
                  Download Receipt
                </Button>
              }
            >
              <iframe
                src={receiptPdfUrl}
                style={{
                  width: "100%",
                  height: "500px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                }}
                title="Receipt Preview"
              />
            </Card>
          ) : (
            <Card
              title={
                <Space>
                  <FilePdfOutlined />
                  <span>Deposit Receipt</span>
                </Space>
              }
              style={{ marginBottom: 16 }}
            >
              <div style={{ textAlign: "center", padding: "20px" }}>
                <Text type="secondary">
                  Receipt PDF is being generated. You can download it using the
                  button below.
                </Text>
                <div style={{ marginTop: "12px" }}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadReceipt}
                  >
                    Download Receipt
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Message Templates */}
          <Card
            title={
              <Space>
                <MessageOutlined />
                <span>Communication Templates</span>
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Tabs defaultActiveKey="whatsapp">
              <TabPane
                tab={
                  <Space>
                    <MessageOutlined />
                    <span>WhatsApp</span>
                  </Space>
                }
                key="whatsapp"
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Copy this template to send via WhatsApp
                  </Text>
                </div>
                <TextArea
                  value={whatsappTemplate}
                  readOnly
                  rows={10}
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={whatsappCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyWhatsApp}
                  >
                    {whatsappCopied ? "Copied!" : "Copy WhatsApp Template"}
                  </Button>
                </div>
              </TabPane>

              <TabPane
                tab={
                  <Space>
                    <MailOutlined />
                    <span>Email</span>
                  </Space>
                }
                key="email"
              >
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Copy this template to send via Email
                  </Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Subject: </Text>
                  <Text code>{emailTemplate.subject}</Text>
                </div>
                <TextArea
                  value={emailTemplate.body}
                  readOnly
                  rows={12}
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={emailCopied ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyEmail}
                  >
                    {emailCopied ? "Copied!" : "Copy Email Template"}
                  </Button>
                </div>
              </TabPane>
            </Tabs>
          </Card>

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Button type="primary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DepositReceiptViewModal;
